import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Banknote, Building2, ShoppingCart, CreditCard, Wallet, Receipt,
  Landmark, Clock, Zap, Layers, Lock, RefreshCw, AlertCircle, Plug,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW: Funds flow diagram — U.S. Bank-powered instant rails
// Route: /preview/funds-flow
//
// Architecture (Feb 2026 update — per user direction):
//   • Plaid          → bank auth (links the buyer's bank, never moves money)
//   • Card gateway   → buyer card / debit top-up (PCI tokenized)
//   • U.S. Bank → sponsored bank digital wallet — where the funds SIT
//                      on-platform. This is the wallet itself; balances live
//                      inside U.S. Bank accounts, not in our DB.
//   • U.S. Bank          → instant transfer rails:
//                        - buyer wallet → seller wallet (settlement)
//                        - seller wallet → seller's destination bank (payout)
//
// Two.inc (BNPL) and Allianz Trade (TCI) are REMOVED from this diagram. They
// were vendor-bundled financing/insurance options; the new architecture keeps
// the rails minimal and instant.
// ─────────────────────────────────────────────────────────────────────────────
export default function FundsFlowPreview() {
  const [stage, setStage] = useState("full"); // full | inflow | settle | payout

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-bold">T</div>
            <span className="display font-semibold">TRAQO Preview · funds flow</span>
          </Link>
          <div className="flex items-center gap-1 text-xs flex-wrap">
            {[
              ["full", "Full picture"],
              ["inflow", "1. Buyer → wallet"],
              ["settle", "2. Wallet → wallet (U.S. Bank)"],
              ["payout", "3. Wallet → bank (U.S. Bank)"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setStage(v)}
                data-testid={`flow-stage-${v}`}
                className={`px-3 py-1.5 rounded-full border transition-colors ${
                  stage === v
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-foreground/70 hover:text-foreground"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
          How money moves on TRAQO
        </div>
        <h1 className="display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] mb-4">
          Buyer pays. Wallet holds. Seller withdraws — instantly.
        </h1>
        <p className="text-foreground/70 max-w-3xl leading-relaxed mb-10">
          Funds live in a <strong>U.S. Bank</strong> wallet (a dedicated DDA per
          counterparty) on the platform. <strong>U.S. Bank</strong> moves money
          instantly — buyer wallet to seller wallet at checkout, and seller
          wallet to their bank on cash-out. No batches, no T+1.
        </p>

        {stage === "full" && <FullDiagram />}
        {stage === "inflow" && <InflowZoom />}
        {stage === "settle" && <SettleZoom />}
        {stage === "payout" && <PayoutZoom />}

        <ProviderTable />

        <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
          <Link to="/" className="text-sm text-foreground/60 hover:text-foreground">← Back to home</Link>
          <Link to="/flow-of-funds" className="text-xs text-foreground/55 hover:text-foreground">
            Banking-partner spec → /flow-of-funds
          </Link>
        </div>
      </main>
    </div>
  );
}

// ─── Full picture (3 columns) ────────────────────────────────────────────────
function FullDiagram() {
  return (
    <div className="rounded-2xl border border-border bg-background p-6 sm:p-10">
      <div className="grid lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
        {/* COL 1 — Buyer inflows */}
        <Column
          title="1. Buyer funds in"
          subtitle="top-up methods"
          accent="amber"
          actor="Buyer"
        >
          <Method icon={Landmark}    label="Bank ACH (Plaid + U.S. Bank)" sub="instant via U.S. Bank · $0 fee · default" pill="default"/>
          <Method icon={CreditCard}  label="Card (debit/credit)"      sub="PCI-tokenized · instant · 2.9% + 30¢"/>
          <Method icon={Receipt}     label="Wire (over $25k)"          sub="U.S. Bank wire-in · same day · $15 flat"/>
          <Method icon={RefreshCw}   label="Claim refund credit"       sub="Bilateral RMA settlement · ledger entry · instant"/>
        </Column>

        <Arrow />

        {/* COL 2 — U.S. Bank wallet */}
        <Column
          title="2. U.S. Bank wallet"
          subtitle="U.S. Bank digital wallet"
          accent="emerald"
          actor="On-platform"
        >
          <div className="rounded-xl border-2 border-foreground bg-foreground text-background p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent">
              <Wallet className="w-3.5 h-3.5" />
              U.S. Bank wallet (USD)
            </div>
            <div className="text-[11px] text-background/70 leading-relaxed">
              The wallet itself <em>is</em> U.S. Bank. Balances are real
              dollars sitting in our U.S. Bank account — not a synthetic
              ledger. Each user's available balance is a sub-account inside the
              same parent.
            </div>
            <div className="border-t border-background/15 pt-3">
              <div className="text-xs uppercase tracking-widest text-accent flex items-center gap-1.5">
                <Building2 className="w-3 h-3" />
                What lives inside
              </div>
              <ul className="mt-1.5 space-y-1 text-[11px] text-background/80">
                <li className="flex gap-1.5"><span className="text-accent">·</span>Buyer balance — funds available to spend</li>
                <li className="flex gap-1.5"><span className="text-accent">·</span>Seller balance — funds available to withdraw</li>
                <li className="flex gap-1.5"><span className="text-accent">·</span>Reserves on disputed orders (held)</li>
                <li className="flex gap-1.5"><span className="text-accent">·</span>Platform fees collected</li>
              </ul>
            </div>
            <div className="border-t border-background/15 pt-3">
              <div className="text-xs uppercase tracking-widest text-accent flex items-center gap-1.5">
                <Zap className="w-3 h-3" />
                U.S. Bank moves between sub-accounts
              </div>
              <div className="text-[11px] text-background/70 mt-1 leading-relaxed">
                When a buyer pays an invoice, U.S. Bank debits the buyer sub-account
                and credits the seller sub-account in the same U.S. Bank wallet.
                Settlement is sub-second.
              </div>
            </div>
          </div>
        </Column>

        <Arrow />

        {/* COL 3 — Seller outflows */}
        <Column
          title="3. Seller cashes out"
          subtitle="instant withdrawals"
          accent="indigo"
          actor="Seller"
        >
          <Method icon={Zap}         label="Instant payout (U.S. Bank)"   sub="seller wallet → seller bank · seconds · 0.5% (cap $5)" pill="default"/>
          <Method icon={Banknote}    label="Standard ACH"             sub="next-business-day · $0 fee · for non-RTP-eligible banks"/>
          <Method icon={Receipt}     label="Wire transfer"            sub="U.S. Bank wire-out · same day · $25 flat (>$10k)"/>
          <Method icon={Plug}        label="Auto-payout schedule"     sub="set & forget · daily/weekly · respects min balance"/>
          <Method icon={Layers}      label="Held funds"               sub="disputed orders / U.S. Bank reversal window · auto-released" pill="held"/>
        </Column>
      </div>

      {/* Below the chart — 3 mini-rails */}
      <div className="mt-10 grid sm:grid-cols-3 gap-3 text-xs">
        {[
          { icon: Clock, t: "End-to-end timing", s: "Buyer pays Mon 2:14pm → seller wallet credited Mon 2:14pm (U.S. Bank wallet→wallet) → seller hits 'Cash out' Mon 2:15pm → settled in seller's bank Mon 2:15pm. Same minute, end to end." },
          { icon: Zap, t: "U.S. Bank rail", s: "RTP + FedNow + same-day ACH fallback. U.S. Bank picks the fastest available rail per receiving bank automatically. 24/7/365 — including weekends and holidays." },
          { icon: AlertCircle, t: "Reversal window", s: "U.S. Bank holds a 24h soft-reverse window on top-ups. If a bank reversal comes in (NSF, fraud), the seller wallet is auto-clawed first; if insufficient, the buyer's score is impacted." },
        ].map((r, i) => (
          <div key={i} className="rounded-lg border border-border p-4">
            <r.icon className="w-4 h-4 text-foreground/60 mb-2" />
            <div className="text-sm font-medium">{r.t}</div>
            <div className="text-foreground/65 mt-1 leading-relaxed">{r.s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Zoom: Inflow ────────────────────────────────────────────────────────────
function InflowZoom() {
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50/30 p-6 sm:p-10">
      <div className="text-xs uppercase tracking-widest text-amber-800 mb-2">
        Stage 1 of 3
      </div>
      <h2 className="display text-2xl font-semibold mb-1">Buyer → U.S. Bank wallet</h2>
      <p className="text-foreground/65 text-sm mb-8 max-w-2xl">
        4 ways funds flow into the buyer's U.S. Bank wallet sub-account.
        The default for most buyers is Plaid-linked bank with U.S. Bank moving the
        money instantly. Cards exist for cash-flow emergencies.
      </p>

      <div className="space-y-3">
        {[
          { icon: Landmark,    name: "Bank ACH (Plaid + U.S. Bank)", provider: "Plaid (auth) → U.S. Bank (move)",            time: "Instant (sub-second)",            fee: "$0",                 note: "Default. Buyer connects bank once via Plaid Link, then any future top-up is moved by U.S. Bank over RTP/FedNow into their U.S. Bank sub-account. Falls back to same-day ACH if the bank isn't on an instant rail." },
          { icon: CreditCard,  name: "Card (debit/credit)",       provider: "Card gateway (PCI tokenized)", time: "Instant authorisation; 2-3d settle", fee: "2.9% + $0.30",       note: "Card data never touches our servers — the tokenization vault posts directly to the card network. We store only the last-4 + brand. Funds appear in the wallet immediately on auth; chargeback window stays open 60d." },
          { icon: Receipt,     name: "Wire transfer (>$25k)",     provider: "U.S. Bank wire-in",                  time: "Same day if before 5pm ET",         fee: "$15 flat",           note: "Buyer initiates from their bank to TRAQO's U.S. Bank account; reference field carries the buyer ID for auto-allocation into their U.S. Bank sub-account." },
          { icon: RefreshCw,   name: "Claim refund credit",       provider: "TRAQO internal (ledger entry)",         time: "Instant",                            fee: "$0",                 note: "Bilateral RMA settled with refund or partial-credit outcome → seller sub-account debits, buyer sub-account credits inside the same U.S. Bank wallet. No external rail used." },
        ].map((m, i) => (
          <div key={i} className="rounded-xl border border-border bg-background p-5 grid grid-cols-12 gap-4 items-start">
            <div className="col-span-1 grid place-items-center">
              <div className="w-10 h-10 rounded-lg bg-foreground/10 grid place-items-center">
                <m.icon className="w-4 h-4" />
              </div>
            </div>
            <div className="col-span-5">
              <div className="text-sm font-medium">{m.name}</div>
              <div className="text-[12px] text-foreground/55 mt-0.5">{m.provider}</div>
              <div className="text-[12px] text-foreground/65 mt-2 leading-relaxed">{m.note}</div>
            </div>
            <div className="col-span-3 text-xs">
              <div className="text-foreground/50 uppercase tracking-widest text-[10px]">Settle time</div>
              <div className="font-medium mt-0.5">{m.time}</div>
            </div>
            <div className="col-span-3 text-xs">
              <div className="text-foreground/50 uppercase tracking-widest text-[10px]">Fee</div>
              <div className="font-medium mt-0.5">{m.fee}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Zoom: Settlement (wallet → wallet) ─────────────────────────────────────
function SettleZoom() {
  return (
    <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/30 p-6 sm:p-10">
      <div className="text-xs uppercase tracking-widest text-emerald-800 mb-2">
        Stage 2 of 3
      </div>
      <h2 className="display text-2xl font-semibold mb-1">Buyer wallet → Seller wallet</h2>
      <p className="text-foreground/65 text-sm mb-8 max-w-2xl">
        U.S. Bank moves the money between sub-accounts inside U.S. Bank.
        Sub-second settlement — the seller sees the credit before the buyer
        even closes the checkout dialog.
      </p>

      {/* Animated flow */}
      <div className="rounded-xl border border-border bg-background p-6">
        <div className="grid grid-cols-3 gap-4 items-center">
          <div className="rounded-lg bg-foreground/[0.04] border border-border p-4 text-center">
            <ShoppingCart className="w-5 h-5 mx-auto mb-2 text-foreground/60" />
            <div className="text-xs uppercase tracking-widest text-foreground/50">Buyer wallet</div>
            <div className="display text-2xl mt-1">−$10,000</div>
            <div className="text-[11px] text-foreground/55 mt-1">at checkout</div>
          </div>
          <div className="text-center">
            <Zap className="w-6 h-6 mx-auto text-emerald-600" />
            <div className="text-[11px] text-foreground/55 mt-1">U.S. Bank<br/>~250ms</div>
          </div>
          <div className="rounded-lg bg-accent/15 border-2 border-foreground p-4 text-center">
            <Wallet className="w-5 h-5 mx-auto mb-2" />
            <div className="text-xs uppercase tracking-widest text-foreground/65">Seller wallet</div>
            <div className="display text-2xl mt-1">+$9,700</div>
            <div className="text-[11px] text-foreground/65 mt-1">net of $300 platform fee</div>
          </div>
        </div>

        {/* Fee breakdown */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border p-3 text-xs">
            <div className="text-foreground/50 uppercase tracking-widest text-[10px] mb-1">Platform fee</div>
            <div className="text-sm font-medium">3.0% default · per-seller override</div>
            <div className="text-foreground/55 mt-0.5">Skimmed before the seller wallet is credited. Bilateral who-pays setting (seller-pays default · buyer can absorb).</div>
          </div>
          <div className="rounded-lg border border-border p-3 text-xs">
            <div className="text-foreground/50 uppercase tracking-widest text-[10px] mb-1">U.S. Bank rail fee</div>
            <div className="text-sm font-medium">$0 wallet→wallet</div>
            <div className="text-foreground/55 mt-0.5">Internal sub-account moves don't cost U.S. Bank anything — no NACHA, no RTP fee. Only outgoing-to-bank legs are billed.</div>
          </div>
        </div>
      </div>

      {/* Payment intents */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-foreground/55 mb-3">
          When the seller actually sees the money — by payment intent
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { intent: "Wallet (instant)",     desc: "Buyer has wallet balance ≥ invoice → U.S. Bank moves the funds at checkout · seller credited in <1s · T+0",        confidence: "100%" },
            { intent: "ACH-per-order",        desc: "Buyer signed U.S. Bank autopay → debit fires from buyer bank at checkout · wallet credited instantly · seller credited same moment", confidence: "high" },
            { intent: "Net-30 / 60 / 90",     desc: "Platform advances seller at T+0 (U.S. Bank to seller wallet) · buyer obligation captured · auto-debit on due date via U.S. Bank", confidence: "advanced" },
            { intent: "Seller-financed",      desc: "Free terms set by seller · seller wallet credited at original payment date · platform takes no advance",       confidence: "deferred" },
            { intent: "Card top-up + pay",    desc: "Buyer tops up via card (instant auth) → U.S. Bank moves balance to seller wallet · same flow, ~3s end-to-end", confidence: "instant" },
            { intent: "Refund / RMA credit",  desc: "Bilateral claim resolved with refund → U.S. Bank reverses seller→buyer sub-accounts · same U.S. Bank wallet · instant", confidence: "instant" },
          ].map((p, i) => (
            <div key={i} className="rounded-lg border border-border bg-background p-4">
              <div className="text-sm font-medium flex items-center justify-between">
                {p.intent}
                <span className="text-[10px] uppercase tracking-widest bg-foreground/5 text-foreground/60 px-2 py-0.5 rounded-full">{p.confidence}</span>
              </div>
              <div className="text-[12px] text-foreground/65 mt-1 leading-relaxed">{p.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Zoom: Payout (seller → seller bank) ────────────────────────────────────
function PayoutZoom() {
  return (
    <div className="rounded-2xl border-2 border-indigo-300 bg-indigo-50/30 p-6 sm:p-10">
      <div className="text-xs uppercase tracking-widest text-indigo-800 mb-2">
        Stage 3 of 3
      </div>
      <h2 className="display text-2xl font-semibold mb-1">Seller wallet → Seller's destination bank</h2>
      <p className="text-foreground/65 text-sm mb-8 max-w-2xl">
        4 ways the seller can move money out. U.S. Bank instant payout is the
        default — funds land in the seller's bank in seconds, 24/7. Standard
        ACH stays free for non-RTP-eligible banks.
      </p>

      <div className="space-y-3">
        {[
          { icon: Zap,      name: "Instant payout (U.S. Bank)", provider: "U.S. Bank over RTP / FedNow", time: "Seconds (24/7/365)", fee: "0.5% (cap $5)", note: "Default for everyone. Seller hits 'Cash out' on /seller/wallet → U.S. Bank picks the fastest rail to their receiving bank → funds available in seconds. Works weekends, evenings, holidays." },
          { icon: Banknote, name: "Standard ACH", provider: "U.S. Bank over same-day ACH (NACHA)", time: "Same business day if before 4pm ET", fee: "$0", note: "Free fallback for sellers whose bank isn't on RTP/FedNow yet. U.S. Bank batches it into the next NACHA window." },
          { icon: Receipt,  name: "Wire transfer", provider: "U.S. Bank wire-out (Fedwire)", time: "Same day if before 5pm ET", fee: "$25 flat", note: "Unlocks at $10k withdrawal threshold. Useful for large payouts and international counterparts (FX layer Q3 2026)." },
          { icon: Plug,     name: "Auto-payout schedule", provider: "Internal scheduler · uses U.S. Bank instant", time: "Configurable (daily / weekly / monthly)", fee: "0.5% per payout (cap $5)", note: "Set-and-forget. Seller picks day(s) of week + minimum balance threshold (e.g. 'every Monday if balance ≥ $1k'). Each scheduled payout runs through U.S. Bank instant." },
        ].map((m, i) => (
          <div key={i} className="rounded-xl border border-border bg-background p-5 grid grid-cols-12 gap-4 items-start">
            <div className="col-span-1 grid place-items-center">
              <div className="w-10 h-10 rounded-lg bg-foreground/10 grid place-items-center">
                <m.icon className="w-4 h-4" />
              </div>
            </div>
            <div className="col-span-5">
              <div className="text-sm font-medium">{m.name}</div>
              <div className="text-[12px] text-foreground/55 mt-0.5">{m.provider}</div>
              <div className="text-[12px] text-foreground/65 mt-2 leading-relaxed">{m.note}</div>
            </div>
            <div className="col-span-3 text-xs">
              <div className="text-foreground/50 uppercase tracking-widest text-[10px]">Time to bank</div>
              <div className="font-medium mt-0.5">{m.time}</div>
            </div>
            <div className="col-span-3 text-xs">
              <div className="text-foreground/50 uppercase tracking-widest text-[10px]">Fee</div>
              <div className="font-medium mt-0.5">{m.fee}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Held funds */}
      <div className="mt-6 rounded-xl border-2 border-dashed border-foreground/30 bg-foreground/[0.02] p-5">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-foreground/60 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium">Held / reserved funds</div>
            <div className="text-[12px] text-foreground/65 mt-1 leading-relaxed">
              Not all wallet balance is withdrawable. Funds are <strong>held</strong> when:
              (1) a claim is open against the underlying order — held until settled,
              (2) the order is within the carrier's POD window — held 48h post-delivery to absorb chargeback risk,
              (3) the underlying buyer top-up is still inside U.S. Bank's 24h reversal window (only on first-time / large top-ups).
              The seller wallet shows two figures: <strong>Available</strong> (withdrawable via U.S. Bank) + <strong>Pending</strong> (held).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Provider table (always visible at the bottom) ──────────────────────────
function ProviderTable() {
  return (
    <section className="mt-12 rounded-2xl border border-border bg-background p-6 sm:p-8">
      <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
        Who runs each rail
      </div>
      <h2 className="display text-2xl font-semibold mb-5">
        3 providers, no overlap
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-foreground/[0.03] text-[11px] uppercase tracking-widest text-foreground/60">
            <tr>
              <th className="text-left px-4 py-2.5">Provider</th>
              <th className="text-left px-4 py-2.5">Role on TRAQO</th>
              <th className="text-left px-4 py-2.5">What it touches</th>
              <th className="text-left px-4 py-2.5">Status today</th>
            </tr>
          </thead>
          <tbody>
            {[
              { p: "Plaid",          r: "Bank auth + identity + cashflow signals", t: "Buyer side only — links the bank, never moves money", s: "Live · sandbox keys",    tone: "emerald" },
              { p: "Card gateway",   r: "Card / debit top-up (PCI tokenized)",     t: "Buyer-side card rail only — funds the buyer's DDA",     s: "Live · sandbox keys",    tone: "emerald" },
              { p: "U.S. Bank",      r: "Per-counterparty DDAs + instant rails (RTP / FedNow / same-day ACH / wire)", t: "Holds the actual dollars in a dedicated DDA per buyer & seller · moves money DDA→DDA and out to banks", s: "In integration", tone: "amber" },
            ].map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{r.p}</td>
                <td className="px-4 py-3 text-foreground/75">{r.r}</td>
                <td className="px-4 py-3 text-foreground/65">{r.t}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                    r.tone === "emerald" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                  }`}>{r.s}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 text-[11px] text-foreground/55 leading-relaxed">
        ⓘ The card gateway is the only provider that touches buyer-card data, and it's PCI tokenized —
        card numbers never reach our servers. U.S. Bank holds the actual cash in a dedicated DDA per
        counterparty, moves money DDA→DDA (free, internal) and out to seller banks
        (RTP / FedNow, seconds, 24/7).
      </div>
    </section>
  );
}

// ─── Reusable bits ────────────────────────────────────────────────────────────
function Column({ title, subtitle, accent, actor, children }) {
  const tone = {
    amber:   "border-amber-300 bg-amber-50/30",
    emerald: "border-emerald-300 bg-emerald-50/30",
    indigo:  "border-indigo-300 bg-indigo-50/30",
  }[accent] || "border-border bg-background";
  return (
    <div className={`rounded-2xl border-2 ${tone} p-5 flex flex-col`}>
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-foreground/50">{actor}</div>
        <div className="display text-lg font-semibold mt-0.5">{title}</div>
        <div className="text-[11px] text-foreground/55">{subtitle}</div>
      </div>
      <div className="space-y-2 flex-1">
        {children}
      </div>
    </div>
  );
}

function Method({ icon: Icon, label, sub, pill }) {
  return (
    <div className="rounded-lg bg-background border border-border p-3 flex items-start gap-3">
      <div className="w-8 h-8 rounded-md bg-foreground/5 grid place-items-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-foreground/65" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
          {label}
          {pill === "default" && <span className="text-[9px] uppercase tracking-widest bg-foreground text-background px-1.5 py-0.5 rounded-full">default</span>}
          {pill === "held" && <span className="text-[9px] uppercase tracking-widest bg-foreground/10 text-foreground/60 px-1.5 py-0.5 rounded-full">held</span>}
        </div>
        <div className="text-[11px] text-foreground/55 mt-0.5 leading-snug">{sub}</div>
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div className="hidden lg:flex items-center justify-center px-2">
      <div className="flex flex-col items-center gap-1">
        <ArrowRight className="w-5 h-5 text-foreground/40" />
        <div className="text-[10px] uppercase tracking-widest text-foreground/40">flow</div>
      </div>
    </div>
  );
}
